import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService } from '@next/core-lib/i18n';
import { AgrSharedModule } from '@next/core-lib/shared';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { AnalysisType, MeasurementAnalysis } from '../../models/measurements.model';

interface MeasurementAnalysisRow {
  id: string;
  label: string;
  grams: number;
}

@Component({
  selector: 'app-measurement-analysis',
  templateUrl: './measurement-analysis.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    AgrFormModule,
    AgrSharedModule,
  ],
})
export class MeasurementAnalysisComponent implements OnInit, OnDestroy {
  private readonly localeService = inject(LocaleService);

  @ViewChild('percentageColumn') percentageColumn: TemplateRef<unknown>;

  @Input()
  analysis$: Observable<MeasurementAnalysis>;
  @Input()
  required = true;

  @Output()
  analysisIsValidChange: EventEmitter<boolean> = new EventEmitter();
  @Output()
  analysisChange: EventEmitter<MeasurementAnalysis> = new EventEmitter();

  formArray: FormArray<FormControl<number>> = new FormArray([]);
  totalGramsInputIsMoreThenZero = signal(true);

  dataSource = new MatTableDataSource<MeasurementAnalysisRow>([]);
  readonly maxAnalysisPercentage = 100;

  readonly analysisGramsChange$ = new Subject<{ formArray: FormArray<FormControl<number>> }>();
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.updateAnalysisValidity();

    this.analysis$
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(
          (prev, curr) => !curr || JSON.stringify(prev) === JSON.stringify(curr),
        ),
      )
      .subscribe((analysis) => {
        if (this.dataSource.data.length > 0) {
          this.updateDataSource(analysis);
        } else {
          this.dataSource = new MatTableDataSource(this.createDataSource(analysis));
        }

        this.updateFormArray();
      });

    this.analysisGramsChange$
      .pipe(
        withLatestFrom(this.analysis$),
        tap(() => this.updateAnalysisValidity()),
        filter(
          ([newAnalysisGrams, analysis]) =>
            analysis?.sieveA?.grams !== newAnalysisGrams.formArray?.value[0] ||
            analysis?.sieveB?.grams !== newAnalysisGrams.formArray?.value[1] ||
            analysis?.sieveC?.grams !== newAnalysisGrams.formArray?.value[2] ||
            analysis?.containerD?.grams !== newAnalysisGrams.formArray?.value[3],
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(([newAnalysisGrams, analysis]) =>
        this.processAnalysisChange(newAnalysisGrams.formArray, analysis.type),
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackById(_: number, item: MeasurementAnalysisRow) {
    return item.id;
  }

  public updateAnalysisGrams() {
    this.analysisGramsChange$.next({ formArray: this.formArray });
  }

  private updateFormArray() {
    this.formArray.clear();
    const validators = this.getValidators();

    this.dataSource.data.forEach((item) => {
      const formControl = new FormControl(item.grams, validators);

      this.formArray.push(formControl);
    });
  }

  private createDataSource(analysis: MeasurementAnalysis): MeasurementAnalysisRow[] {
    return [
      {
        id: 'sieveA',
        label: this.localeService.translate('measurements.wizard.analysis.sieve-a'),
        grams: analysis?.sieveA?.grams,
      },
      {
        id: 'sieveB',
        label: this.localeService.translate('measurements.wizard.analysis.sieve-b'),
        grams: analysis?.sieveB?.grams,
      },
      {
        id: 'sieveC',
        label: this.localeService.translate('measurements.wizard.analysis.sieve-c'),
        grams: analysis?.sieveC?.grams,
      },
      {
        id: 'containerD',
        label: this.localeService.translate('measurements.wizard.analysis.container-d'),
        grams: analysis?.containerD?.grams,
      },
    ];
  }

  private updateDataSource(analysis: MeasurementAnalysis) {
    this.dataSource.data[0].grams = analysis.sieveA?.grams;
    this.dataSource.data[1].grams = analysis.sieveB?.grams;
    this.dataSource.data[2].grams = analysis.sieveC?.grams;
    this.dataSource.data[3].grams = analysis.containerD?.grams;
  }

  private processAnalysisChange(formArray: FormArray, type: AnalysisType) {
    this.analysisChange.emit({
      type,
      sieveA: {
        grams: formArray.value[0],
      },
      sieveB: {
        grams: formArray.value[1],
      },
      sieveC: {
        grams: formArray.value[2],
      },
      containerD: {
        grams: formArray.value[3],
      },
      total: this.formArray.value.reduce((a, b) => a + b, 0),
    });
  }

  private getValidators(): ValidatorFn[] {
    const validators = [Validators.min(0), Validators.max(9999)];
    if (this.required) {
      validators.push(Validators.required);
    }
    return validators;
  }

  private analysisIsValid(): boolean {
    return this.formArray.valid && this.totalGramsInputIsMoreThenZero();
  }

  private updateAnalysisValidity(): void {
    this.totalGramsInputIsMoreThenZero.set(
      this.isTotalGramsInputMoreThenZero(this.formArray.value),
    );
    this.analysisIsValidChange.emit(this.analysisIsValid());
  }

  private isTotalGramsInputMoreThenZero(values: number[]): boolean {
    if (values.some((value) => typeof value !== 'number') || values.length === 0) {
      return true;
    }

    return values.reduce((accumulator: number, current: number) => accumulator + current, 0) > 0;
  }
}
